import { format as fnsFormat, isToday, isYesterday, isThisYear, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';

/* Toda la interfaz está en español: sin pasar el idioma, date-fns escribía los
   meses en inglés ("12 Aug" en vez de "12 ago"). */
const format = (fecha, patron) => fnsFormat(fecha, patron, { locale: es });

/* Horas a 12 h en todo el portal («5:05 p. m.»), como el calendario (decisión de Gabriel, 2026-10-06).
   date-fns escribe «PM» en español: se hace a mano para que coincida con lib/calendario.js. */
export function hora12(fecha) {
  const d = fecha instanceof Date ? fecha : new Date(fecha);
  if (isNaN(d)) return '';
  const h = d.getHours();
  return `${h % 12 || 12}:${String(d.getMinutes()).padStart(2, '0')} ${h < 12 ? 'a. m.' : 'p. m.'}`;
}

/* «6 oct 2026, 5:05 p. m.» (o sin año). */
export function fechaHora12(fecha, { anio = true } = {}) {
  const d = fecha instanceof Date ? fecha : new Date(fecha);
  if (isNaN(d)) return '';
  return `${format(d, anio ? 'd MMM yyyy' : 'd MMM')}, ${hora12(d)}`;
}

export function fmtDate(iso) {
  if (!iso) return '';
  let d;
  try { d = typeof iso === 'string' ? parseISO(iso) : iso; } catch { return ''; }
  if (isNaN(d)) return '';
  if (isToday(d)) return hora12(d);
  if (isYesterday(d)) return 'Ayer';
  if (isThisYear(d)) return format(d, 'd MMM');
  return format(d, 'dd/MM/yy');
}

export function fmtFull(iso) {
  if (!iso) return '';
  try {
    const d = typeof iso === 'string' ? parseISO(iso) : iso;
    if (isNaN(d)) return '';
    return fechaHora12(d);
  } catch { return ''; }
}

/* "hoy 10:32 a. m." · "ayer 6:04 p. m." · "12 ago, 9:11 a. m.".
   Para el visto de lectura: la hora importa siempre, el día solo cuando no es hoy. */
export function fmtMomento(iso) {
  if (!iso) return '';
  try {
    const d = typeof iso === 'string' ? parseISO(iso) : iso;
    if (isNaN(d)) return '';
    if (isToday(d)) return `hoy ${hora12(d)}`;
    if (isYesterday(d)) return `ayer ${hora12(d)}`;
    if (isThisYear(d)) return fechaHora12(d, { anio: false });
    return fechaHora12(d);
  } catch { return ''; }
}

export function dateGroup(iso) {
  if (!iso) return 'Anteriores';
  try {
    const d = parseISO(iso);
    if (isToday(d)) return 'Hoy';
    if (isYesterday(d)) return 'Ayer';
    const diff = (Date.now() - d.getTime()) / 86400000;
    if (diff < 7) return 'Esta semana';
    if (diff < 30) return 'Este mes';
    return 'Anteriores';
  } catch { return 'Anteriores'; }
}

/** Iniciales de una persona.
 *  "Gabriel Vega" → GV · "Gabriel" → GA
 *  "gabriel.vega@x.com" → GV · "gabriel@orvian.mx" → GA
 *  Del correo se usa SOLO la parte local: el dominio no dice nada de la persona
 *  (antes "gabriel@orvian.mx" daba "GO", con la O de orvian). */
export function initials(name, email) {
  const clean = (name || '').trim();
  if (clean) {
    const words = clean.split(/\s+/).filter(Boolean);
    if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
    return words[0].slice(0, 2).toUpperCase();
  }
  const local = (email || '').split('@')[0] || '?';
  const parts = local.split(/[._\-+]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return (parts[0] || local).slice(0, 2).toUpperCase();
}

const COLORS = ['#0B7F77','#1B6FB0','#B7791F','#8E44AD','#C0392B','#16A085','#2C7A7B','#9B2C2C','#2B6CB0','#6B46C1'];
export function avatarColor(seed) {
  const s = seed || '';
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % COLORS.length;
  return COLORS[Math.abs(h)];
}

export function humanSize(bytes) {
  if (!bytes) return '0 B';
  const u = ['B','KB','MB','GB'];
  let i = 0; let n = bytes;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
}
